import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CollectionsPage = () => {
  const [collections, setCollections] = useState([]);
  const [filter, setFilter] = useState("All");
  const [loading, setLoading] = useState(true);

  const categories = ["All", "Wedding", "Evening Wear", "Client Photos"];

  useEffect(() => {
    const fetchCollections = async () => {
      try {
        const response = await axios.get(`${API}/collections`);
        setCollections(response.data);
      } catch (error) {
        console.error("Error fetching collections:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchCollections();
  }, []);

  const filteredCollections = filter === "All" 
    ? collections 
    : collections.filter(item => item.category === filter);

  return (
    <div data-testid="collections-page" className="min-h-screen pt-20">
      {/* Header */}
      <section className="py-20 md:py-28 border-b border-white/5">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <p className="mono-text text-primary mb-6">Our Portfolio</p>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight mb-6" data-testid="collections-page-title">
              Collections
            </h1>
            <p className="text-muted-foreground max-w-xl mx-auto font-light">
              Explore our curated selection of custom creations, each piece a testament 
              to the art of fine tailoring.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Filters */}
      <section className="py-8 border-b border-white/5 sticky top-20 z-30 glass">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12">
          <div className="flex flex-wrap gap-4 justify-center" data-testid="collection-filters">
            {categories.map((category) => (
              <button
                key={category}
                data-testid={`filter-${category.toLowerCase().replace(" ", "-")}`}
                onClick={() => setFilter(category)}
                className={`px-6 py-2 text-xs uppercase tracking-[0.2em] transition-all duration-300 ${
                  filter === category
                    ? "bg-primary text-primary-foreground"
                    : "border border-white/10 text-muted-foreground hover:border-primary hover:text-primary"
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Collection Grid */}
      <section className="py-16 md:py-24">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="aspect-[3/4] bg-card animate-pulse" />
              ))}
            </div>
          ) : (
            <motion.div 
              className="collection-grid"
              layout
            >
              {filteredCollections.map((item, index) => (
                <motion.div
                  key={item.id}
                  data-testid={`collection-card-${index}`}
                  className="collection-item group cursor-pointer"
                  layout
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ duration: 0.5, delay: index * 0.05 }}
                >
                  <img
                    src={item.image_url}
                    alt={item.title}
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                  <div className="collection-item-overlay" />
                  <div className="absolute inset-0 p-6 flex flex-col justify-end">
                    <p className="mono-text text-primary text-[10px] mb-2">{item.category}</p>
                    <h3 className="font-serif text-xl mb-2 group-hover:text-primary transition-colors">
                      {item.title}
                    </h3>
                    <p className="text-sm text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      {item.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          )}

          {!loading && filteredCollections.length === 0 && (
            <div className="text-center py-20">
              <p className="text-muted-foreground">No items found in this category.</p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default CollectionsPage;
